import React, { useEffect, useRef, useState } from "react";
import { MinimalCase } from "@/src/types/Entities/MinimalCase";
import { Conversation } from "@/src/types/Entities/Messaging/Conversation";
import _orderBy from "lodash/orderBy";
import Grid from "@mui/material/Grid";
import { Box, Pagination, Stack, TableHead, TableSortLabel } from "@mui/material";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import ConversationTableRowGroup from "./ConversationTableRowGroup";
import Button from "@mui/material/Button";
import { Link, useLocation } from "react-router";
import MessageCenterConversation from "./MessageCenterConversation";
import { getNewMessageLink } from "@/src/services/messagingService";
import EmailVerificationModal from "../../FormInfoModal/EmailVerificationModal";
import MessageCenterNoCasesMessage from "./MessageCenterNoCasesMessage";
import MessagesCasePicker from "./MessagesCasePicker";
import MessageCenterMessageReply from "./MessageCenterMessageReply";
import IncompleteCaseWarning from "./IncompleteCaseWarning";
import ProductBox from "@/src/components/ProductBox/ProductBox";
import { messageCenterStyle } from "@/src/components/Messaging/MessageCenter/messageCenterStyle";
import { getProductData } from "@/src/services/productService";
import Icon from "@/src/components/Icon/Icon";
import { EmailOutlined } from "@mui/icons-material";
import { mergeSx } from "@/src/utils/mergeSx";

type MessageCenterProps = {
  minimalCases: Array<MinimalCase>;
  conversations: Array<Conversation>;
};

const ITEMS_PER_PAGE = 20;

export default function MessageCenter({ minimalCases, conversations }: MessageCenterProps) {
  const [selectedCase, setSelectedCase] = useState<MinimalCase | null>(null);
  const [page, setPage] = useState(0);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [openConversationId, setOpenConversationId] = useState<number | null>(null);
  const messagesBoxRef = useRef<null | HTMLDivElement>(null!);
  const openConversation: Conversation | undefined = conversations.find((conversation) => conversation.id === openConversationId);
  const { search, state } = useLocation();
  const urlSearchParams = new URLSearchParams(search);

  useEffect(() => {
    if (state?.resetOpenConversation) {
      setOpenConversationId(null);
    }
  }, [state?.resetOpenConversation]);

  useEffect(() => {
    if (minimalCases && minimalCases.length > 0) {
      const initialCaseId = urlSearchParams.get("caseId");
      const initialSelectedCase = minimalCases.find((minimalCase) => minimalCase.id + "" === initialCaseId);

      if (initialSelectedCase) {
        setSelectedCase(initialSelectedCase);
        return;
      }
      const orderedMinimalCases = _orderBy(minimalCases, ["created"], ["desc"]);
      setSelectedCase(orderedMinimalCases[0]);
    }
  }, [minimalCases]);

  const sortComparisonFunction = (conversationA: Conversation, conversationB: Conversation) => {
    if (conversationA.latestMessageCreatedDate < conversationB.latestMessageCreatedDate) {
      return sortDirection === "desc" ? 1 : -1;
    }
    if (conversationA.latestMessageCreatedDate > conversationB.latestMessageCreatedDate) {
      return sortDirection === "asc" ? 1 : -1;
    }
    return 0;
  };

  const filteredConversations = conversations.filter((conversation) => selectedCase && conversation.channel.caseId === selectedCase.id);
  let sortedFilteredConversations = filteredConversations;
  sortedFilteredConversations.sort(sortComparisonFunction);

  const handlePaginationChange = (e: React.ChangeEvent<unknown>, page: number) => {
    setPage(page - 1);
    scrollToMessagesBox();
  };

  const scrollToMessagesBox = () => {
    messagesBoxRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const currentIndexDerivedFromPage = page * ITEMS_PER_PAGE;
  const paginatedFilteredConversations = sortedFilteredConversations.slice(currentIndexDerivedFromPage, currentIndexDerivedFromPage + ITEMS_PER_PAGE);
  const handleConversationClick = (minimalCase: MinimalCase) => {
    setSelectedCase(minimalCase);
    setOpenConversationId(null);
    setPage(0);
    scrollToMessagesBox();
  };

  return (
    <>
      <Grid container spacing={2}>
        {minimalCases.length > 0 && (
          <Grid size={12}>
            <Box id={"casePicker"} />
            <MessagesCasePicker handleConversationClick={handleConversationClick} selectedCase={selectedCase} />
          </Grid>
        )}
        <Grid size={12}>
          <Box ref={messagesBoxRef} />
          {openConversation && selectedCase && (
            <>
              <MessageCenterConversation
                conversation={openConversation}
                setOpenConversationId={setOpenConversationId}
                key={openConversation.id}
                product={selectedCase}
              />
              <MessageCenterMessageReply product={selectedCase} conversation={openConversation} />
            </>
          )}
          {!openConversation && (
            <ProductBox variant={"hintbox"} headline={"Nachrichten"}>
              {!!selectedCase && (
                <Typography sx={{ textOverflow: "ellipsis", overflow: "hidden", marginBottom: "1.5rem" }}>
                  {getProductData(selectedCase.name, "productCategory.label") + " - " + selectedCase.reference}
                </Typography>
              )}
              {minimalCases.length === 0 && <MessageCenterNoCasesMessage />}
              {selectedCase && <IncompleteCaseWarning product={selectedCase} />}
              {selectedCase && paginatedFilteredConversations.length === 0 && (
                <Stack sx={{ alignItems: "center", textAlign: "center", padding: "1.5rem 0 1.5rem" }}>
                  <Icon icon={({ fill }) => <EmailOutlined fontSize={"large"} sx={{ fill }} />} variant={"bordered"} />
                  <Typography
                    variant={"cardTitle"}
                    component={"div"}
                    sx={{
                      margin: "1rem 0 1rem",
                    }}
                  >
                    Keine Nachrichten für diesen Fall vorhanden
                  </Typography>
                  <Typography>
                    Sobald wir Sie zu diesem Fall kontaktieren, finden Sie hier <br /> alle Nachrichten gebündelt.
                  </Typography>
                </Stack>
              )}
              {minimalCases.length !== 0 && (
                <Box sx={!selectedCase ? { pointerEvents: "none" } : {}}>
                  {paginatedFilteredConversations.length !== 0 && (
                    <>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell scope={"col"} sx={mergeSx(messageCenterStyle.sortRowColumn, { display: { xs: "none", md: "table-cell" } })} />
                            <TableCell scope={"col"} sx={messageCenterStyle.sortRowColumn} />
                            <TableCell scope={"col"} sx={messageCenterStyle.sortRowColumn} width={"80px"}>
                              <TableSortLabel
                                active
                                direction={sortDirection}
                                onClick={() => setSortDirection((prevState) => (prevState === "asc" ? "desc" : "asc"))}
                              >
                                Datum
                              </TableSortLabel>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        {paginatedFilteredConversations.map((conversation) => (
                          <ConversationTableRowGroup setOpenConversationId={setOpenConversationId} key={conversation.id} conversation={conversation} />
                        ))}
                      </Table>
                      <Stack
                        sx={{
                          alignItems: "center",
                          margin: "2rem",
                        }}
                      >
                        <Pagination count={Math.ceil(filteredConversations.length / ITEMS_PER_PAGE)} onChange={handlePaginationChange} />
                      </Stack>
                    </>
                  )}
                </Box>
              )}
              <Grid
                container
                direction={"row"}
                spacing={2}
                sx={{
                  justifyContent: "space-evenly",
                  flexDirection: "row-reverse",
                  paddingTop: "1rem",
                }}
              >
                <Grid
                  size={{
                    xs: 12,
                    sm: 6,
                  }}
                >
                  <Stack direction={"row"} sx={{ justifyContent: "flex-end" }}>
                    <Button
                      disabled={!selectedCase}
                      component={Link}
                      to={getNewMessageLink(selectedCase?.name || "", selectedCase?.id || 0)}
                      variant={"contained"}
                      sx={messageCenterStyle.actionLink}
                    >
                      Neue Nachricht senden
                    </Button>
                  </Stack>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    sm: 6,
                  }}
                >
                  <Stack direction={"row"} sx={{ justifyContent: "flex-start" }}>
                    <Button component={Link} variant={"outlined"} to={"/servicewelt"} sx={messageCenterStyle.actionLink}>
                      Zurück zu “Meine Fälle”
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </ProductBox>
          )}
        </Grid>
      </Grid>
      <EmailVerificationModal />
    </>
  );
}
